// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCompareChanges.test.tsx
//
// SPIKE (side-by-side version compare). The left column while the comparison
// shows its changes: one card per changed window, under the number the window
// carries on both artworks, the selected one opened into a diff -- the window
// cut from either version, removed words tinted red and added ones green by
// warhol, and the edits as diff lines.
import { type AskPunttComparisonRegion } from "@mg/schemas/src/christo/versionComparison";
import { Crosshair } from "@phosphor-icons/react";
import { Badge, Button, Checkbox, Text } from "@radix-ui/themes";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { type DiffRow, diffRows, regionTitle } from "./versionChanges";

type AskV2ReviewCompareChangesProps = {
  regions: readonly AskPunttComparisonRegion[];
  /** The window both artworks stand on, by its place in `regions`. */
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  /**
   * Bring both artworks back to a window already selected. why its own
   * callback: selecting the selected window again changes no state, so the
   * viewers would not move.
   */
  onLocate: (region: AskPunttComparisonRegion) => void;
  /** How the older and the newer version are named on screen ("V1", "V2"). */
  beforeMark: string;
  afterMark: string;
  /** Windows a person marked reviewed, by number; absent hides the control. */
  reviewed?: ReadonlySet<number>;
  onToggleReviewed?: (number: number, reviewed: boolean) => void;
};

const ROW_STYLE: Record<DiffRow["kind"], string> = {
  removed: "bg-puntt-red-3 text-puntt-red-11",
  added: "bg-malachite-100 text-malachite-800",
  moved: "bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11",
  context: "text-puntt-neutral-gray-11",
};

const ROW_SIGN: Record<DiffRow["kind"], string> = {
  removed: "−",
  added: "+",
  moved: "↔",
  context: "",
};

function evidenceLabel(
  region: AskPunttComparisonRegion,
  t: (key: string) => string,
): string {
  if (region.evidence === "pixels") return t("askV2.review.compareKindPicture");
  if (region.evidence === "moved") return t("askV2.review.compareKindMoved");
  return t("askV2.review.compareKindWording");
}

function Side({
  region,
  side,
  mark,
}: {
  region: AskPunttComparisonRegion;
  side: "before" | "after";
  mark: string;
}) {
  const { t } = useTranslation("ui");
  const image = region[side];
  const label =
    side === "before"
      ? t("askV2.review.compareBeforeLabel", { mark })
      : t("askV2.review.compareAfterLabel", { mark });
  return (
    <div className="min-w-0 flex-1">
      <div
        className={`flex items-center justify-between px-2 py-1 text-[12px] font-medium ${
          side === "before"
            ? "bg-puntt-red-2 text-puntt-red-11"
            : "bg-malachite-50 text-malachite-700"
        }`}
      >
        <span>
          {side === "before" ? "− " : "+ "}
          {label}
        </span>
        <span>{t("askV2.review.comparePage", { page: region.page })}</span>
      </div>
      {image ? (
        <img
          src={image.source}
          alt={t("askV2.review.compareCutAlt", {
            mark,
            number: region.number,
          })}
          className="block w-full border border-puntt-neutral-gray-4"
          data-testid={`ask-v2-review-compare-change-${side}-${region.number}`}
        />
      ) : (
        <div
          className="flex h-20 items-center justify-center border border-dashed border-puntt-neutral-gray-5 text-[12px] text-puntt-neutral-gray-10"
          data-testid={`ask-v2-review-compare-change-${side}-missing-${region.number}`}
        >
          {t("askV2.review.compareNotInVersion")}
        </div>
      )}
    </div>
  );
}

export function AskV2ReviewCompareChanges({
  regions,
  selectedIndex,
  onSelect,
  onLocate,
  beforeMark,
  afterMark,
  reviewed,
  onToggleReviewed,
}: AskV2ReviewCompareChangesProps) {
  const { t } = useTranslation("ui");
  const selectedRef = useRef<HTMLLIElement | null>(null);

  // why: a change stepped to with the arrows or picked on an artwork has to
  // come into view here too, or the column says nothing about it.
  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex]);

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      aria-label={t("askV2.review.compareChangesLabel")}
      data-testid="ask-v2-review-compare-changes"
    >
      <div className="px-4 py-3">
        <Text as="p" size="3" weight="bold">
          {t("askV2.review.compareChangeCount", { count: regions.length })}
        </Text>
      </div>
      <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 pb-4">
        {regions.map((region, index) => {
          const open = index === selectedIndex;
          const title = regionTitle(region) ?? evidenceLabel(region, t);
          const rows = diffRows(region);
          const isReviewed = reviewed?.has(region.number) ?? false;
          return (
            <li
              key={region.number}
              ref={open ? selectedRef : undefined}
              className={`rounded-lg border bg-base-white ${
                open
                  ? "border-puntt-blue-8 shadow-sm"
                  : "border-puntt-neutral-gray-5"
              }`}
              data-testid={`ask-v2-review-compare-change-card-${region.number}`}
              data-selected={open ? "true" : "false"}
            >
              <div className="flex items-center gap-2 px-3 py-2">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  aria-expanded={open}
                  data-testid={`ask-v2-review-compare-change-open-${region.number}`}
                  onClick={() => onSelect(index)}
                >
                  <span className="font-mono shrink-0 text-[12px] text-puntt-neutral-gray-10">
                    {String(region.number).padStart(2, "0")}
                  </span>
                  <span className="min-w-0 truncate text-[14px] font-medium text-puntt-neutral-gray-12">
                    {title}
                  </span>
                  <Badge size="1" variant="soft" color="gray">
                    {evidenceLabel(region, t)}
                  </Badge>
                </button>
                {onToggleReviewed ? (
                  <Text
                    as="label"
                    size="1"
                    className="flex shrink-0 items-center gap-1 text-puntt-neutral-gray-11"
                  >
                    <Checkbox
                      size="1"
                      checked={isReviewed}
                      data-testid={`ask-v2-review-compare-change-reviewed-${region.number}`}
                      onCheckedChange={(checked) =>
                        onToggleReviewed(region.number, checked === true)
                      }
                    />
                    {t("askV2.review.compareMarkReviewed")}
                  </Text>
                ) : null}
              </div>
              {open ? (
                <div
                  className="border-t border-puntt-neutral-gray-4 px-3 pb-3 pt-2"
                  data-testid={`ask-v2-review-compare-change-diff-${region.number}`}
                >
                  <div className="flex gap-2">
                    <Side region={region} side="before" mark={beforeMark} />
                    <Side region={region} side="after" mark={afterMark} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[12px] text-puntt-neutral-gray-10">
                    <span>{t("askV2.review.compareLegend")}</span>
                    <Button
                      variant="ghost"
                      size="1"
                      data-testid={`ask-v2-review-compare-change-locate-${region.number}`}
                      onClick={() => onLocate(region)}
                    >
                      <Crosshair size={14} aria-hidden="true" />
                      {t("askV2.review.compareLocate")}
                    </Button>
                  </div>
                  {rows.length > 0 ? (
                    <ol className="font-mono mt-2 space-y-0.5 text-[12px]">
                      {rows.map((row, rowIndex) => (
                        <li
                          // why the index: one window can list the same words twice.
                          key={`${row.kind}-${rowIndex}`}
                          className={`flex gap-2 rounded px-2 py-0.5 ${ROW_STYLE[row.kind]}`}
                          data-testid={`ask-v2-review-compare-change-row-${region.number}-${rowIndex}`}
                          data-kind={row.kind}
                        >
                          {/* why aria-hidden + sr-only: a name on a role-less span never reaches the tree */}
                          <span className="w-3 shrink-0" aria-hidden="true">
                            {ROW_SIGN[row.kind]}
                          </span>
                          {row.kind === "context" ? null : (
                            <span className="sr-only">
                              {t(`askV2.review.compareRow_${row.kind}`)}:
                            </span>
                          )}
                          <span className="min-w-0 break-words">
                            {row.text}
                          </span>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {region.changesMore > 0 ? (
                    <Text as="p" size="1" color="gray" className="mt-1">
                      {t("askV2.review.compareMoreEdits", {
                        count: region.changesMore,
                      })}
                    </Text>
                  ) : null}
                  {region.readByOcr ? (
                    <Text
                      as="p"
                      size="1"
                      color="gray"
                      className="mt-1"
                      data-testid={`ask-v2-review-compare-change-ocr-${region.number}`}
                    >
                      {t("askV2.review.compareReadByOcr")}
                    </Text>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
